import React, { useState, useEffect } from 'react';
import './RetryModal.css';

interface RetryModalProps {
    isOpen: boolean;
    onClose: () => void;
    queryData: {
        question: string;
        error: string;
    };
    onRetry: (editedQuestion: string) => void;
}

export const RetryModal: React.FC<RetryModalProps> = ({
    isOpen,
    onClose,
    queryData,
    onRetry
}) => {
    const [editedQuestion, setEditedQuestion] = useState(queryData.question);

    // Reset when modal opens with new data
    useEffect(() => {
        if (isOpen) {
            setEditedQuestion(queryData.question);
        }
    }, [isOpen, queryData.question]);

    if (!isOpen) return null;

    const handleSubmit = () => {
        if (!editedQuestion.trim()) return;
        onRetry(editedQuestion.trim());
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose();
        }
    };

    return (
        <>
            <div className="modal-backdrop" onClick={onClose} />
            <div className="retry-modal-container" onKeyDown={handleKeyDown}>
                <div className="retry-modal-content">
                    {/* Header */}
                    <div className="retry-modal-header">
                        <h3>Retry Query</h3>
                        <button onClick={onClose} className="close-button" aria-label="Close modal">
                            ×
                        </button>
                    </div>

                    {/* Error Context */}
                    <div className="retry-error-context">
                        <span className="material-symbols-outlined">error</span>
                        <div>
                            <strong>Previous Error:</strong>
                            <p>{queryData.error}</p>
                        </div>
                    </div>

                    {/* Editable Query */}
                    <div className="retry-query-editor">
                        <label htmlFor="retry-query-input">Edit your query:</label>
                        <textarea
                            id="retry-query-input"
                            value={editedQuestion}
                            onChange={(e) => setEditedQuestion(e.target.value)}
                            rows={4}
                            placeholder="Enter your question..."
                            autoFocus
                        />
                        <span className="hint-text">
                            💡 You can modify the query to fix errors or improve clarity
                        </span>
                    </div>

                    {/* Actions */}
                    <div className="retry-modal-footer">
                        <button onClick={onClose} className="retry-button-cancel">
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!editedQuestion.trim()}
                            className="retry-button-submit"
                        >
                            <span className="material-symbols-outlined">send</span>
                            Send Query
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
